import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const MASTER_CONNECTOR_API_URL = `${API_BASE_URL}synctrack/master-connector`;
const SYNC_CONNECTORS_API_URL = `${API_BASE_URL}synctrack/sync-connectors`;
const TEST_API_PROXY_URL = `${API_BASE_URL}synctrack/test-api-proxy`;

const TestAPI = () => {
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('');
  const [headers, setHeaders] = useState([{ key: '', value: '' }]);
  const [bodyRaw, setBodyRaw] = useState('{\n  \n}');
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  const [connectorTokens, setConnectorTokens] = useState([]);
  const [selectedConnectorId, setSelectedConnectorId] = useState('');
  const [connectorsLoading, setConnectorsLoading] = useState(true);
  const [useProxy, setUseProxy] = useState(true);
  const [corsProxyUrl, setCorsProxyUrl] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [headersOpen, setHeadersOpen] = useState(false);
  const [paramsOpen, setParamsOpen] = useState(false);
  const [queryParams, setQueryParams] = useState([{ key: '' }]);

  const hasBody = ['POST', 'PUT', 'PATCH'].includes(method);

  useEffect(() => {
    const loadConnectors = async () => {
      setConnectorsLoading(true);
      try {
        const userToken = localStorage.getItem('token');
        const tenantId = localStorage.getItem('tenantId');
        if (!userToken) {
          setConnectorTokens([]);
          return;
        }
        const authHeader = { Authorization: `Bearer ${userToken}` };

        const [masterRes, tokensRes] = await Promise.all([
          fetch(MASTER_CONNECTOR_API_URL, { headers: { Accept: 'application/json', ...authHeader } }),
          fetch(`${SYNC_CONNECTORS_API_URL}/tenant/${tenantId || ''}`, { headers: { Accept: 'application/json', ...authHeader } }),
        ]);

        if (!masterRes.ok || !tokensRes.ok) {
          setConnectorTokens([]);
          return;
        }

        const masterData = await masterRes.json();
        const tokensData = await tokensRes.json();

        const mapped = (tokensData || []).map((item) => {
          const mc = (masterData || []).find(
            (m) => String(m.mcRecId) === String(item.mcId) || String(m.mcId) === String(item.mcId)
          );
          const mcName = mc ? mc.mcName : `Connector (${item.mcId})`;
          const mcConnectorType = mc?.mcConnectorType ?? null;
          const description = item.description || mc?.description || '';
          return { ...item, id: item.id, mcId: item.mcId, mcName, mcConnectorType, description };
        });
        setConnectorTokens(mapped);
        if (mapped.length && !selectedConnectorId) setSelectedConnectorId(mapped[0].id);
      } catch (err) {
        console.error('Error loading connectors:', err);
        toast.error('Failed to load connector tokens');
        setConnectorTokens([]);
      } finally {
        setConnectorsLoading(false);
      }
    };
    loadConnectors();
  }, []);

  const selectedConnector = connectorTokens.find((c) => c.id === selectedConnectorId);

  const addHeaderRow = () => {
    setHeaders((prev) => [...prev, { key: '', value: '' }]);
  };

  const removeHeaderRow = (index) => {
    if (headers.length <= 1) return;
    setHeaders((prev) => prev.filter((_, i) => i !== index));
  };

  const updateHeader = (index, field, value) => {
    setHeaders((prev) =>
      prev.map((h, i) => (i === index ? { ...h, [field]: value } : h))
    );
  };

  const addParamRow = () => {
    setQueryParams((prev) => [...prev, { key: '' }]);
  };

  const removeParamRow = (index) => {
    if (queryParams.length <= 1) return;
    setQueryParams((prev) => prev.filter((_, i) => i !== index));
  };

  const updateParam = (index, value) => {
    setQueryParams((prev) =>
      prev.map((p, i) => (i === index ? { key: value } : p))
    );
  };

  const buildHeadersObject = () => {
    const obj = {};
    headers.forEach(({ key, value }) => {
      if (key?.trim()) obj[key.trim()] = value?.trim() ?? '';
    });
    return obj;
  };

  const getAuthHeader = () => {
    if (selectedConnector?.token) {
      return { Authorization: `Bearer ${selectedConnector.token}` };
    }
    const token = localStorage.getItem('token');
    if (token) return { Authorization: `Bearer ${token}` };
    return {};
  };

  const handleSend = async () => {
    const trimmedUrl = url?.trim();
    if (!trimmedUrl) {
      toast.warning('Please enter a URL');
      return;
    }

    setLoading(true);
    setResponse(null);
    setError(null);

    const startTime = Date.now();

    try {
      const requestHeaders = {
        ...getAuthHeader(),
        ...buildHeadersObject(),
      };

      if (useProxy) {
        // Pass connectorId + connectorType (mcConnectorType from master-connector).
        const connectorTypeValue = selectedConnector?.mcConnectorType || null;
        const payload = {
          method: method.toUpperCase(),
          url: trimmedUrl,
          headers: Object.fromEntries(
            Object.entries(requestHeaders).filter(([k]) => k.toLowerCase() !== 'authorization')
          ),
          connectorId: selectedConnectorId || null,
          connectorType: selectedConnectorId ? connectorTypeValue : null,
          bearerToken: selectedConnectorId ? null : (requestHeaders.Authorization?.replace(/^Bearer\s+/i, '') || null),
          queryParams: queryParams.map(p => p.key).filter(k => k && k.trim() !== ''),
        };
        if (hasBody && bodyRaw?.trim()) {
          try {
            payload.body = JSON.parse(bodyRaw);
          } catch {
            payload.body = bodyRaw;
          }
        }
        const userToken = localStorage.getItem('token');
        const proxyRes = await axios.post(TEST_API_PROXY_URL, payload, {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userToken}` },
          validateStatus: () => true,
        });
        const duration = Date.now() - startTime;
        if (proxyRes.status >= 400) {
          setError({
            message: proxyRes.data?.message || `Proxy error: ${proxyRes.status}`,
            code: 'PROXY_ERROR',
            duration,
            response: {
              status: proxyRes.status,
              statusText: proxyRes.statusText,
              data: proxyRes.data,
              headers: proxyRes.headers,
            },
          });
          setResponse(null);
        } else {
          const { status, statusText, headers: resHeaders, data } = proxyRes.data || {};
          setResponse({
            status: status ?? proxyRes.status,
            statusText: statusText ?? proxyRes.statusText,
            duration,
            headers: resHeaders || {},
            data: data !== undefined ? data : proxyRes.data,
          });
        }
        setActiveTab(0);
        setLoading(false);
        return;
      }

      const finalUrl =
        !useProxy && corsProxyUrl?.trim()
          ? corsProxyUrl.trim().replace(/\/?$/, '/') + trimmedUrl.replace(/^\//, '')
          : trimmedUrl;

      let config = {
        method: method.toLowerCase(),
        url: finalUrl,
        headers: requestHeaders,
        validateStatus: () => true,
      };

      if (hasBody && bodyRaw?.trim()) {
        try {
          config.data = JSON.parse(bodyRaw);
        } catch {
          config.data = bodyRaw;
          if (!requestHeaders['Content-Type']) {
            requestHeaders['Content-Type'] = 'text/plain';
          }
        }
      }

      const res = await axios(config);
      const duration = Date.now() - startTime;

      setResponse({
        status: res.status,
        statusText: res.statusText,
        duration,
        headers: res.headers,
        data: res.data,
      });
      setActiveTab(0);
    } catch (err) {
      const duration = Date.now() - startTime;
      setError({
        message: err.message || 'Request failed',
        code: err.code,
        duration,
        response: err.response
          ? {
            status: err.response.status,
            statusText: err.response.statusText,
            data: err.response.data,
            headers: err.response.headers,
          }
          : null,
      });
      setResponse(null);
      setActiveTab(0);
    } finally {
      setLoading(false);
    }
  };

  const formatJson = (data) => {
    if (data === null || data === undefined) return 'null';
    if (typeof data === 'string') {
      try {
        return JSON.stringify(JSON.parse(data), null, 2);
      } catch {
        return data;
      }
    }
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));
  };

  const responseStatusColor = (status) => {
    if (!status) return 'text.primary';
    if (status >= 200 && status < 300) return 'success.main';
    if (status >= 300 && status < 400) return 'info.main';
    if (status >= 400 && status < 500) return 'warning.main';
    return 'error.main';
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        p: 3,
      }}
    >
      {/* <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 3, flexShrink: 0 }}>
        Test API
      </Typography> */}

      <Paper sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 340, maxWidth: 480, flex: '0 0 auto' }}>
            <InputLabel id="connector-select-label">Connector (Bearer Token)</InputLabel>
            <Select
              labelId="connector-select-label"
              label="Connector (Bearer Token)"
              value={selectedConnectorId}
              onChange={(e) => setSelectedConnectorId(e.target.value)}
              disabled={connectorsLoading}
              sx={{ backgroundColor: 'grey.50' }}
            >
              <MenuItem value="">
                <em>None (use login token)</em>
              </MenuItem>
              {connectorTokens.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.description ? `${c.mcName} — ${c.description}` : c.mcName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={advancedOpen}
                onChange={(e) => setAdvancedOpen(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="body2">Advanced</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={headersOpen}
                onChange={(e) => setHeadersOpen(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="body2">Headers</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={paramsOpen}
                onChange={(e) => setParamsOpen(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="body2">Params</Typography>}
          />
        </Box>

        {advancedOpen && (
          <Box sx={{ mb: 2, pl: 0 }}>
            <Tooltip title="Use your backend proxy to avoid CORS. Turn off if you use a local CORS proxy below.">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useProxy}
                    onChange={(e) => setUseProxy(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    Use backend proxy (avoids CORS)
                  </Typography>
                }
                sx={{ mb: 1, display: 'block' }}
              />
            </Tooltip>
            {!useProxy && (
              <Tooltip title="Run a local CORS proxy (e.g. npx cors-anywhere) and enter its URL here. Requests will go: browser → proxy → target API. Leave blank to send directly (may hit CORS).">
                <TextField
                  fullWidth
                  size="small"
                  label="CORS proxy URL (optional, no backend needed)"
                  placeholder="e.g. http://localhost:8080/"
                  value={corsProxyUrl}
                  onChange={(e) => setCorsProxyUrl(e.target.value)}
                  sx={{ mb: 1, maxWidth: 480, '& .MuiOutlinedInput-root': { backgroundColor: 'grey.50' } }}
                  helperText="Run: npx cors-anywhere (then use http://localhost:8080/). Proxy forwards your URL and headers (including Bearer)."
                />
              </Tooltip>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              sx={{
                height: 40,
                backgroundColor: method === 'GET' ? 'success.light' : 'primary.light',
                color: method === 'GET' ? 'success.dark' : 'primary.contrastText',
                fontWeight: 600,
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
            >
              {HTTP_METHODS.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            placeholder="https://api.example.com/endpoint"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            sx={{
              flex: '1 1 300px',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'grey.50',
                height: 40,
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={loading}
            sx={{
              minWidth: 100,
              height: 40,
              alignSelf: 'stretch',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {loading ? 'Sending...' : 'Send'}
          </Button>
        </Box>

        <Box sx={{ mb: (hasBody || headersOpen || paramsOpen) ? 2 : 0 }}>
          {paramsOpen && (
            <Box sx={{ mb: headersOpen ? 2 : 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                Query Parameters (Values resolved from backend)
              </Typography>
              <Table size="small" sx={{ mb: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queryParams.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ py: 0.5 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="e.g. access_token"
                          value={row.key}
                          onChange={(e) => updateParam(index, e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'grey.50' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <IconButton size="small" onClick={() => removeParamRow(index)} disabled={queryParams.length <= 1}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button size="small" startIcon={<AddIcon />} onClick={addParamRow}>
                Add param
              </Button>
            </Box>
          )}

          {headersOpen && (
            <>
              <Table size="small" sx={{ mb: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '40%', fontWeight: 600 }}>Key</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {headers.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ py: 0.5 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Content-Type"
                          value={row.key}
                          onChange={(e) => updateHeader(index, 'key', e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'grey.50' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="application/json"
                          value={row.value}
                          onChange={(e) => updateHeader(index, 'value', e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'grey.50' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <IconButton size="small" onClick={() => removeHeaderRow(index)} disabled={headers.length <= 1}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button size="small" startIcon={<AddIcon />} onClick={addHeaderRow}>
                Add header
              </Button>
            </>
          )}
        </Box>

        {hasBody && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600, color: 'text.secondary' }}>
              Body (raw JSON)
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={6}
              maxRows={12}
              value={bodyRaw}
              onChange={(e) => setBodyRaw(e.target.value)}
              placeholder='{"key": "value"}'
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  backgroundColor: 'grey.50',
                },
              }}
            />
          </>
        )}
      </Paper>

      <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 420 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Response" />
          <Tab label="Headers" />
        </Tabs>

        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">{error.message}</Typography>
              {error.response && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Status: {error.response.status} {error.response.statusText} · Time: {error.duration} ms
                </Typography>
              )}
              {error.response?.status === 401 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Token may have expired. Go to <strong>Connector Token</strong> (Sync Flows menu), find this connector, and refresh/re-connect to get a new token.
                </Typography>
              )}
            </Alert>
          )}

          {response && activeTab === 0 && (
            <>
              {response.status === 401 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Token may have expired. Go to <strong>Connector Token</strong> (Sync Flows menu), find this connector, and refresh/re-connect to get a new token.
                </Alert>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography
                  component="span"
                  sx={{ fontWeight: 600, color: responseStatusColor(response.status) }}
                >
                  {response.status} {response.statusText}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Time: {response.duration} ms
                </Typography>
                <IconButton size="small" onClick={() => copyToClipboard(formatJson(response.data))} title="Copy response">
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Box>
              <TextField
                fullWidth
                multiline
                minRows={18}
                maxRows={50}
                value={formatJson(response.data)}
                InputProps={{ readOnly: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'monospace',
                    fontSize: '0.8125rem',
                    backgroundColor: 'grey.50',
                  },
                }}
              />
            </>
          )}

          {response && activeTab === 1 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Header</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {response.headers &&
                  Object.entries(response.headers).map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{k}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', wordBreak: 'break-all' }}>
                        {typeof v === 'string' ? v : String(v)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}

          {error?.response && activeTab === 1 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Header</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {error.response.headers &&
                  Object.entries(error.response.headers).map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{k}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', wordBreak: 'break-all' }}>
                        {typeof v === 'string' ? v : String(v)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}

          {!response && !error && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: 'text.secondary' }}>
              <Typography variant="body2">Send a request to see the response here.</Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default TestAPI;
